import { useEffect, useMemo, useState } from 'react'
import { DataTable, DateRangeChip, FilterBar, FilterChip, Money, type ColumnDef } from '../components'
import { useAccountMovement, type MovementFilters } from '../lib/api/movement'
import { useAccounts } from '../lib/api/accounts'
import { useTransaction } from '../lib/api/transactions'
import { useAppState } from '../app-state'
import { fmtMoney } from '../lib/money'
import type { TransactionType } from '../lib/api/types'

type Props = { accountId: string }

function defaultRange(): { from: string; to: string } {
  const today = new Date()
  const to = today.toISOString().slice(0, 10)
  const fromDate = new Date(today)
  fromDate.setMonth(fromDate.getMonth() - 3)
  const from = fromDate.toISOString().slice(0, 10)
  return { from, to }
}

export function Movement({ accountId }: Props) {
  const range = useMemo(defaultRange, [])
  const [dateFrom, setDateFrom] = useState(range.from)
  const [dateTo, setDateTo] = useState(range.to)
  const [type, setType] = useState<TransactionType | ''>('')
  const [search, setSearch] = useState('')

  const accounts = useAccounts()
  const account = accounts.data?.find((a) => a.id === accountId) ?? null

  // Statement rows carry only an id, so clicking one fetches the full transaction
  // and hands it to the shared edit drawer (same form as the operations list).
  const { setDrawerTx } = useAppState()
  const [openId, setOpenId] = useState<string | null>(null)
  const opened = useTransaction(openId)
  useEffect(() => {
    if (openId && opened.data) {
      setDrawerTx({ mode: 'edit', transaction: opened.data })
      setOpenId(null)
    }
  }, [openId, opened.data, setDrawerTx])

  const filters: MovementFilters = {
    dateFrom,
    dateTo,
    type: type || undefined,
    search: search.trim() || undefined,
  }
  const movement = useAccountMovement(accountId, filters)

  const items = movement.data?.items ?? []
  // Statement reads newest-first (most recent at the top). The server returns rows
  // oldest→newest so the running balance accumulates correctly; we only flip the
  // display order, so each row keeps its own running balance. Derive from the
  // query's stable items reference so the memo isn't recomputed every render.
  const displayItems = useMemo(
    () => (movement.data?.items ?? []).slice().reverse(),
    [movement.data?.items],
  )
  const opening = movement.data?.openingBalance ?? 0
  const closing = movement.data?.closingBalance ?? 0
  const currency = account?.currency ?? movement.data?.currency ?? 'UAH'
  const hasContentFilter = !!type || !!search.trim()

  type MovementItem = (typeof items)[number]
  const TYPE_LABEL: Record<string, string> = {
    income: 'Надходження',
    expense: 'Витрата',
    transfer: 'Переказ',
  }
  const columns: ColumnDef<MovementItem>[] = [
    { id: 'date', header: 'Дата', width: 90, cellClassName: 'fg3', cell: (it) => it.date },
    { id: 'type', header: 'Тип', width: 110, cell: (it) => TYPE_LABEL[it.type] ?? it.type },
    {
      id: 'category',
      header: 'Категорія',
      cell: (it) => it.category?.name ?? '—',
    },
    {
      id: 'counterparty',
      header: 'Контрагент',
      cell: (it) => it.counterparty?.name ?? '—',
    },
    { id: 'description', header: 'Опис', cell: (it) => it.description ?? '—' },
    {
      id: 'amount',
      header: 'Сума',
      width: 110,
      thClassName: 'num',
      cellClassName: 'num',
      // `delta` is the signed amount this row moved in the account's own currency
      // (an inbound transfer credits the received amount, not the source leg), so
      // it reads in the statement's currency and lines up with the balance column.
      cell: (it) => (
        <span style={{ color: it.direction === 'out' ? 'var(--danger-fg)' : 'var(--success-fg)' }}>
          <Money amount={it.delta} currency={currency} sign />
        </span>
      ),
    },
    {
      id: 'balance',
      header: 'Баланс',
      width: 140,
      pinned: true,
      thClassName: 'num sticky-right',
      cellClassName: 'num sticky-right',
      cell: (it) => (
        <span style={{ fontWeight: 500 }}>
          <Money amount={it.runningBalance} currency={currency} />
        </span>
      ),
    },
  ]

  return (
    <>
      <FilterBar>
        <FilterChip label="Рахунок" value={account?.name ?? '…'} />
        <DateRangeChip
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => {
            setDateFrom(f)
            setDateTo(t)
          }}
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value as TransactionType | '')}
          style={{
            height: 22,
            background: 'var(--bg-2)',
            border: '1px solid var(--border-default)',
            color: 'var(--fg-1)',
            borderRadius: 6,
            padding: '0 6px',
            fontSize: 11,
            fontFamily: 'inherit',
          }}
        >
          <option value="">Усі типи</option>
          <option value="income">Надходження</option>
          <option value="expense">Витрата</option>
          <option value="transfer">Переказ</option>
        </select>
        <input
          type="text"
          placeholder="Пошук в описі…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            height: 22,
            background: 'var(--bg-2)',
            border: '1px solid var(--border-default)',
            color: 'var(--fg-1)',
            borderRadius: 6,
            padding: '0 6px',
            fontSize: 11,
            fontFamily: 'inherit',
            width: 160,
          }}
        />
      </FilterBar>

      <div className="content">
        <div className="card" style={{ marginBottom: 12, padding: 14 }}>
          <div style={{ display: 'flex', gap: 32, alignItems: 'baseline' }}>
            <div>
              <div
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--fg-3)',
                }}
              >
                Сальдо на початок
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--fg-1)' }}>
                {fmtMoney(opening, currency)}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--fg-3)',
                }}
              >
                Сальдо на кінець
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--fg-1)' }}>
                {fmtMoney(closing, currency)}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--fg-3)' }}>
              {dateFrom} — {dateTo}
            </div>
          </div>
        </div>

        {hasContentFilter && (
          <div
            style={{
              padding: '8px 12px',
              marginBottom: 8,
              fontSize: 11,
              color: 'var(--fg-3)',
              background: 'var(--bg-2)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 4,
            }}
          >
            Баланс показано з урахуванням усіх операцій діапазону, навіть тих, які
            приховано фільтрами.
          </div>
        )}

        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
            {movement.isLoading ? (
              <div style={{ padding: 24, color: 'var(--fg-3)' }}>Завантаження…</div>
            ) : items.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--fg-3)' }}>Немає операцій</div>
            ) : (
              <DataTable
                tableKey="movement"
                columns={columns}
                rows={displayItems}
                rowKey={(it) => it.id}
                onRowClick={(it) => setOpenId(it.id)}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
